import { forwardRef, useCallback } from 'react';

import { useSearchParams } from 'react-router-dom';

import {
  Button,
  DrawerProps,
  Icons,
  useDrawer,
  useDrawerInstance,
  Typography,
  tokens,
} from '@cast/design-system';

import { Drawer, DrawerContent, DrawerHeader } from 'components/drawer';
import { useAbility } from '@cast/shared-ability-data-access';
import {
  useGetSlackConfigurations,
  useGetWebhooksQuery,
} from 'hooks/queries/notifications';

import { useSlackOAuthReturn } from './_hooks';
import { AddAlertDrawer } from './AddAlertDrawer';
import { AlertsList } from './AlertsList';

import {
  SearchProvider,
  UseServerData,
  useSearchContext,
} from '@cast/shared-search-ui';

const useData: UseServerData = ({ sortingState, enabled }) => {
  const {
    webhooks,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useGetWebhooksQuery({ sortingState, enabled });
  const { data: slackConfigurations, isLoading: slackConfigurationsLoading } =
    useGetSlackConfigurations(!!enabled);

  // Combine and sort by name for consistent UI ordering
  const combinedData = [
    ...(slackConfigurations?.items ?? []),
    ...(webhooks ?? []),
  ].sort((a, b) => {
    const nameA = a?.name || '';
    const nameB = b?.name || '';
    return nameA.localeCompare(nameB);
  });

  return {
    data: combinedData,
    isLoading: isLoading || slackConfigurationsLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage: !isLoading && hasNextPage,
    isFetching,
  };
};

const CreateAlertButton = ({ onAddAlert }: { onAddAlert: () => void }) => {
  const ability = useAbility();
  const ctx = useSearchContext();

  // Hide button if there's no data
  if (!ctx?.data?.length) {
    return null;
  }

  return (
    <Button
      onClick={onAddAlert}
      disabled={ability.cannot('create', 'OrganizationWebHooks')}
      size="sm"
      startIcon={<Icons.Plus size={16} />}
      testId="add-alert-button"
    >
      Create alert
    </Button>
  );
};

const AlertsTitle = () => {
  const ctx = useSearchContext();

  // Hide title if there's no data
  if (!ctx?.data?.length) {
    return null;
  }

  return (
    <Typography
      variant="h5"
      pt="12px"
      color={tokens.theme.fg.structural.primary}
    >
      Alerts
    </Typography>
  );
};

export const AlertsDrawer = forwardRef<HTMLDivElement, DrawerProps>(
  ({ open, ...drawerProps }, ref) => {
    const { close } = useDrawerInstance();
    const { open: openDrawer } = useDrawer();
    const [searchParams] = useSearchParams();

    // Fetch data to find alerts when returning from Slack OAuth
    const { webhooks } = useGetWebhooksQuery({ enabled: open });
    const { data: slackConfigurations } = useGetSlackConfigurations(!!open);

    const onAddAlert = useCallback(() => {
      close({
        onComplete: () => {
          openDrawer({
            component: AddAlertDrawer,
            props: {},
          });
        },
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useSlackOAuthReturn({
      open,
      searchParams,
      webhooks,
      slackConfigurations,
      close,
      openDrawer,
    });

    return (
      <SearchProvider useData={useData}>
        <Drawer
          ref={ref}
          size="xsmall"
          open={open}
          onClose={() => close()}
          {...drawerProps}
          overlayWhenSubdrawerActive
          testId="alerts-drawer"
        >
          <DrawerHeader
            noDivider
            sx={{ paddingRight: '16px' }}
            actions={<CreateAlertButton onAddAlert={onAddAlert} />}
          >
            <AlertsTitle />
          </DrawerHeader>
          <DrawerContent
            height="100%"
            sx={{
              padding: 0,
            }}
            contentWrapperProps={{
              sx: {
                height: '100%',
              },
            }}
          >
            <AlertsList />
          </DrawerContent>{' '}
        </Drawer>
      </SearchProvider>
    );
  }
);

AlertsDrawer.displayName = 'AlertsDrawer';
